import type { MessageInputRef } from "@editor/components/editor/ai/chat/MessageInput";
import type { Editor } from "@tiptap/react";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AnimatedBorder from "@editor/components/common/AnimatedBorder";
import Glow from "@editor/components/common/Glow";
import ChatHeader from "@editor/components/editor/ai/chat/ChatHeader";
import useChatMessageLimits from "@editor/components/editor/ai/chat/hooks/useChatMessageLimits";
import { useQuickPrompt } from "@editor/components/editor/ai/chat/hooks/useQuickPrompt";
import ManageChats from "@editor/components/editor/ai/chat/ManageChats";
import { MessageInput } from "@editor/components/editor/ai/chat/MessageInput";
import AssetComponent from "@editor/components/editor/ai/chat/MessageInput/assetNodeExtension";
import ProductComponent from "@editor/components/editor/ai/chat/MessageInput/productNodeExtension";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useInfinitePages } from "@editor/hooks/useInfinitePages";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import HardBreakMarkdown from "@/features/react-editor/components/chat/extensions/hardBreakMarkdown";
import {
  chatEditorHandleKeyDown,
  chatEditorHandlePaste,
  chatEditorOnUpdate,
  getCleanedChatPrompt,
} from "@/features/react-editor/components/chat/utils/tiptapUtils";
import { useChatSidebar } from "@/features/react-editor/hooks/useChatSidebar";
import Button from "@replo/design-system/components/button/Button";
import { InlineSidebar } from "@replo/design-system/components/inline-sidebar/InlineSidebar";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/shadcn/core/tabs";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import { Tag as BetaTag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  AppWindow,
  Image,
  LayoutPanelTop,
  Lightbulb,
  Megaphone,
  Tag,
} from "lucide-react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";

import PagesSection from "./PagesSection";
import PageTemplatesSection from "./PageTemplatesSection";

const quickPrompts = [
  {
    icon: <Image className="w-3.5 h-3.5" />,
    text: "Copy a Screenshot",
    prompt: "build a copy of this screenshot",
    action: "upload",
  },
  {
    icon: <AppWindow className="w-3.5 h-3.5" />,
    text: "Build Landing Page",
    prompt: "Build me a landing page and find some templates I can use",
  },
  {
    icon: <Megaphone className="w-3.5 h-3.5" />,
    text: "Make Page from Ad",
    prompt:
      "generate a landing page that matches the messaging and offer of this ad",
    action: "upload",
  },
  {
    icon: <Tag className="w-3.5 h-3.5" />,
    text: "Build Product Page",
    prompt: "Build me a product page that helps me sell this product: ",
    action: "product",
  },
  {
    icon: <Lightbulb className="w-3.5 h-3.5" />,
    text: "Suggest Ideas",
    prompt: "Suggest some new ideas to help me make more money",
  },
];

const cyclingPlaceholders = [
  "Build me a landing page for Black Friday...",
  "Create a beautiful product page for my new product launch...",
  "Suggest me some ideas to grow my conversion rates...",
  "Match my landing page to my ad...",
  "Create me a section that looks like...",
  "Build me a page based on this screenshot...",
  "Build me a form for collecting emails...",
  "Create me a quiz funnel to help customers make buying decisions...",
  "Build me an on-brand, high-converting landing page...",
  "Build me a mobile first page for DSP traffic...",
];

function HomeChat() {
  const [files, setFiles] = useState<File[] | null>(null);
  const [atPosition, setAtPosition] = useState<number | null>(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  const homeEditorRef = useRef<Editor | null>(null);
  const messageInputRef = useRef<MessageInputRef>(null);

  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const logEvent = useLogAnalytics();

  const { messagesRemaining, hasUnlimitedMessages } = useChatMessageLimits();

  const handleSubmit = useCallback(async () => {
    const editor = homeEditorRef.current;
    if (!editor || !projectId) {
      return;
    }

    if (!hasUnlimitedMessages && messagesRemaining <= 0) {
      return;
    }

    const prompt = getCleanedChatPrompt(editor);
    if (!prompt) {
      return;
    }

    editor.commands.setContent("", false);
    setFiles(null);

    // NOTE (Bobby, 2025-07-30): Storing this through react router's state so in the browser's session history. Potentially want to make this
    // in a shared context but the home and chat were just in pretty unrelated contexts so doing this for now.
    navigate(`/shops/${projectId}/chat`, {
      state: {
        initialMessage: prompt,
        initialFiles: files,
      },
    });
  }, [projectId, navigate, files, messagesRemaining, hasUnlimitedMessages]);

  // Set up the Tiptap editor
  const homeEditor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false,
        paragraph: {
          HTMLAttributes: {
            style: "white-space: pre-wrap",
          },
        },
      }),
      AssetComponent,
      ProductComponent,
      HardBreakMarkdown,
      TiptapMarkdown.configure({
        /*
         * NOTE (Kevin 2025-09-24):
         * breaks=false because the HardBreak extension emits <br /> directly.
         * html=true keeps <chat-token> placeholders intact through markdown.
         * tightLists=true prevents blank paragraphs between list items.
         */
        breaks: false,
        html: true,
        tightLists: true,
      }),
      Placeholder.configure({
        placeholder: "Ask Replo anything...",
      }),
    ],
    content: "",
    onUpdate: chatEditorOnUpdate(setAtPosition, atPosition),
    autofocus: true,
    editorProps: {
      attributes: {
        style: "font-size: 14px; white-space: pre-wrap;",
        class: "home-chat-input",
      },
      handleKeyDown: chatEditorHandleKeyDown(
        setAtPosition,
        atPosition,
        handleSubmit,
      ),
      handlePaste: chatEditorHandlePaste(setFiles),
    },
  });

  // Update the ref whenever the editor changes
  useEffect(() => {
    homeEditorRef.current = homeEditor;
  }, [homeEditor]);

  const [typedPlaceholder, setTypedPlaceholder] = useState("");
  useEffect(() => {
    let cancelled = false;
    const phrase = cyclingPlaceholders[placeholderIndex] ?? "";

    const TYPE_DELAY = 28;
    const DELETE_DELAY = 16;
    const START_DELAY = 250;
    const DWELL_AFTER = 3000;

    let charIndex = 0;
    let typeTimer: number | undefined;
    let dwellTimer: number | undefined;
    let deleteTimer: number | undefined;

    const typeStep = () => {
      if (cancelled) {
        return;
      }
      requestAnimationFrame(() => {
        setTypedPlaceholder(phrase.slice(0, charIndex + 1));
      });
      charIndex += 1;
      if (charIndex < phrase.length) {
        typeTimer = window.setTimeout(typeStep, TYPE_DELAY);
      } else {
        dwellTimer = window.setTimeout(deletePhase, DWELL_AFTER);
      }
    };

    const deletePhase = () => {
      if (cancelled) {
        return;
      }
      const deleteStep = () => {
        if (cancelled) {
          return;
        }
        charIndex -= 1;
        requestAnimationFrame(() => {
          setTypedPlaceholder(phrase.slice(0, charIndex));
        });
        if (charIndex > 0) {
          deleteTimer = window.setTimeout(deleteStep, DELETE_DELAY);
        } else {
          setPlaceholderIndex((i) => (i + 1) % cyclingPlaceholders.length);
        }
      };
      deleteStep();
    };

    const startTimer = window.setTimeout(typeStep, START_DELAY);
    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      if (typeTimer) {
        window.clearTimeout(typeTimer);
      }
      if (dwellTimer) {
        window.clearTimeout(dwellTimer);
      }
      if (deleteTimer) {
        window.clearTimeout(deleteTimer);
      }
    };
  }, [placeholderIndex]);

  const chatPlaceholderStyle = useMemo(() => {
    return {
      ["--chat-placeholder"]: JSON.stringify(typedPlaceholder),
    } as unknown as React.CSSProperties;
  }, [typedPlaceholder]);

  const handleQuickPrompt = useQuickPrompt({
    editor: homeEditor,
    onSelectUploadImage: () => {
      void messageInputRef.current?.triggerFileUpload();
    },
    onSelectProduct: () => {
      void messageInputRef.current?.triggerContextMenu();
    },
  });
  const isMobile = useIsMobile();

  return (
    <div
      className={twMerge(
        "flex flex-col w-full items-center justify-center",
        isMobile ? "gap-8" : "gap-14",
      )}
    >
      <style>
        {`
          .home-chat-input.tiptap p.is-editor-empty:first-child::before {
            color: var(--color-slate-500);
            content: var(--chat-placeholder, "Ask Replo anything...") " ";
            float: left;
            height: 0;
            pointer-events: none;
            white-space: pre;
            margin-right: 0.35ch;
          }

          .home-chat-input.tiptap * {
            max-width: 100%;
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          .home-chat-input.tiptap p {
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
        `}
      </style>
      <div className="flex items-center gap-3">
        <div
          className={twMerge(
            "font-semibold text-default cursor-default",
            isMobile ? "text-3xl" : "text-4xl",
          )}
        >
          What can I help you with?
        </div>
        <BetaTag variant="beta" size="base">
          Beta
        </BetaTag>
      </div>
      <div className="flex flex-col w-full items-center justify-center">
        <div
          className={twMerge(
            "relative w-full max-w-[800px]",
            isMobile ? "px-4" : "px-6",
          )}
        >
          <div className="relative">
            <Glow roundedClassName="rounded-[16px]">
              <AnimatedBorder className="rounded-[16px] re-rounded-6px">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                  }}
                  className="w-full cursor-text h-full bg-white rounded-[16px]"
                  style={chatPlaceholderStyle}
                >
                  <MessageInput
                    ref={messageInputRef}
                    isChatLoading={false}
                    editor={homeEditor}
                    allowAttachments={true}
                    files={files}
                    setFiles={setFiles}
                    editorClassName="min-h-[100px] max-h-[200px]"
                  />
                </form>
              </AnimatedBorder>
            </Glow>
          </div>
        </div>
        <div className="pt-3 relative w-full flex items-center justify-center">
          <div
            className={twMerge(
              "gap-2 max-w-[800px]",
              isMobile
                ? "grid grid-cols-2 w-full px-4 justify-center"
                : "flex flex-wrap justify-center w-full px-0",
            )}
          >
            {(isMobile ? quickPrompts.slice(0, 4) : quickPrompts).map(
              (prompt) => (
                <Button
                  key={prompt.text}
                  size="base"
                  variant="secondary"
                  UNSAFE_className={twMerge(
                    "rounded-lg bg-white hover:bg-sheet transition-colors flex flex-row gap-2 border border-border/60",
                    isMobile
                      ? "w-full justify-center whitespace-normal text-center"
                      : "w-auto justify-start whitespace-nowrap text-left",
                  )}
                  onClick={() => {
                    logEvent("chat.selectRecommendationPrompt", {
                      origin: "home",
                      prompt: prompt.prompt,
                    });
                    handleQuickPrompt(prompt.prompt, prompt.action);
                  }}
                >
                  <div className="pr-2">{prompt.icon}</div>
                  <span className="text-sm">{prompt.text}</span>
                </Button>
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HomeDashboard() {
  const isMobile = useIsMobile();
  const projectId = useCurrentProjectId();
  const { pages, isLoading } = useInfinitePages({
    projectId: projectId ?? null,
    limit: 1,
  });

  /* NOTE (Kevin 2025-09-18): Track user's explicit tab selection separately from computed default.
  This handles the timing issue where useState initializer runs before async data loads,
   so we dynamically compute the default based on whether pages exist. */
  const [userSelectedTab, setUserSelectedTab] = useState<
    "pages" | "templates" | null
  >(null);

  const resolvedDefaultTab: "pages" | "templates" =
    !isLoading && pages?.length === 0 ? "templates" : "pages";

  const selectedTab = userSelectedTab ?? resolvedDefaultTab;

  return (
    <div className="flex flex-col gap-6 h-full">
      <Tabs
        value={selectedTab}
        onValueChange={(value) => {
          if (value === "pages" || value === "templates") {
            setUserSelectedTab(value);
          }
        }}
        className="flex flex-col h-full items-center gap-6"
      >
        <TabsList
          className={twMerge(
            "grid grid-cols-2 bg-surface flex-shrink-0",
            isMobile ? "w-full" : "w-fit",
          )}
        >
          <TabsTrigger
            className={twMerge(
              "border-none shadow-none w-full",
              selectedTab === "pages" && "bg-white border-0.5 border-border",
            )}
            value="pages"
          >
            <span className="inline-flex items-center gap-1">
              <AppWindow className="w-3.5 h-3.5" /> Your Pages
            </span>
          </TabsTrigger>
          <TabsTrigger
            className={twMerge(
              "border-none shadow-none w-full",
              selectedTab === "templates" &&
                "bg-white border-0.5 border-border",
            )}
            value="templates"
          >
            <span className="inline-flex items-center gap-1">
              <LayoutPanelTop className="w-3.5 h-3.5" /> Templates
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pages" className="flex-1 min-h-0 w-full">
          <PagesSection setSelectedTab={setUserSelectedTab} />
        </TabsContent>

        <TabsContent value="templates" className="flex-1 min-h-0 w-full">
          <PageTemplatesSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Home() {
  const projectId = useCurrentProjectId();

  const [searchParams] = useSearchParams();
  const { open: isChatSidebarOpen } = useChatSidebar();
  const isMobile = useIsMobile();

  const chatIdInParams = searchParams.get("chatId");

  if (projectId && searchParams.has("chatId")) {
    const basePath = `/shops/${projectId}/chat`;
    const path = `${basePath}?chatId=${chatIdInParams}`;
    return <Navigate to={path} replace />;
  }

  return (
    <div className="flex h-full w-full">
      {!isMobile && (
        <InlineSidebar
          open={isChatSidebarOpen}
          className={twMerge(
            "w-[250px] border-r-0.5 border-border rounded-l-xl",
            !isChatSidebarOpen && "border-r-transparent",
          )}
        >
          <div className="flex flex-col min-w-[250px] h-full">
            <ManageChats />
          </div>
        </InlineSidebar>
      )}
      <div
        className={twMerge(
          "flex-1 min-h-0 flex flex-col",
          isMobile ? "px-2" : "px-3",
        )}
      >
        <div className="relative">
          <div className="absolute top-3">
            <ChatHeader showTitle={false} />
          </div>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto no-scrollbar"
          id="home-scroll-container"
        >
          <div
            className={twMerge(
              "w-full max-w-[1440px] mx-auto flex flex-col h-full px-3",
              isMobile ? "gap-10" : "gap-16",
              isMobile ? "mt-20" : "mt-24",
            )}
          >
            <HomeChat />
            <HomeDashboard />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
